import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CreateButton } from 'src/shared/button/Buttons'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import {
  HelpInstructionsConfigForm,
  HelpInstructionsConfigFormValues,
} from 'src/screens/administration/configuration-management/help-instructions-configuration/edit/HelpInstructionsConfigForm'
import { Help_And_Instruction_Link_Insert_Input, Mutation_Root } from 'src/@types/graphql'
import { insertHelpInstructionLink } from 'src/screens/administration/configuration-management/help-instructions-configuration/helpInstructionsConfigQueries'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { useHelpAndInstructionLinks } from 'src/service/help-and-instruction-links/useHelpAndInstructionLinks'
import { useSearchParams } from 'react-router-dom'
import { HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM } from 'src/screens/administration/configuration-management/help-instructions-configuration/HelpInstructionsGridUtils'

const defaultInitialValues: HelpInstructionsConfigFormValues = {
  deLabel: '',
  deLink: '',
  enLabel: '',
  enLink: '',
  frLabel: '',
  frLink: '',
  itLabel: '',
  itLink: '',
  key: '',
  locationHints: [],
}

const onCreate = () => {
  document.getElementById('help-instructions-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const HelpInstructionsConfigCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { syncHelpInstructionLinks } = useHelpAndInstructionLinks()
  const [searchParams] = useSearchParams()

  const [initialValues, setInitialValues] = useState<HelpInstructionsConfigFormValues | null>(null)
  const [formDirty, setFormDirty] = useState<boolean | undefined>(undefined)

  const searchParamHelpInstructionsKey = useMemo(
    () => searchParams.get(HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM),
    [searchParams],
  )

  useEffect(() => {
    const initialValuesToSet = { ...defaultInitialValues }

    if (searchParamHelpInstructionsKey) {
      initialValuesToSet.key = searchParamHelpInstructionsKey
    }

    setInitialValues(initialValuesToSet)
  }, [searchParamHelpInstructionsKey])

  const onBackHandler = useCallback(() => {
    if (searchParamHelpInstructionsKey) {
      navigate(-1)
    } else {
      navigate(ROUTES.HelpInstructionsConfigurationIndex.params({}))
    }
  }, [navigate, searchParamHelpInstructionsKey])

  const handleSubmitLocal = useCallback(
    async (values: HelpInstructionsConfigFormValues) => {
      const helpInstructionsInsertInput: Help_And_Instruction_Link_Insert_Input = {
        key: values.key,
        labels: {
          de: values.deLabel ?? '',
          en: values.enLabel ?? '',
          fr: values.frLabel ?? '',
          it: values.itLabel ?? '',
        },
        links: {
          de: values.deLink ?? '',
          en: values.enLink ?? '',
          fr: values.frLink ?? '',
          it: values.itLink ?? '',
        },
        location_hints: values.locationHints ?? [],
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_help_and_instruction_link: Mutation_Root['insert_help_and_instruction_link']
        }>(insertHelpInstructionLink, {
          helpInstructionsInsertInput: [helpInstructionsInsertInput],
        })
        .toPromise()

      if (!error && data?.insert_help_and_instruction_link?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        syncHelpInstructionLinks()
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, syncHelpInstructionLinks, urqlClient],
  )

  return (
    <ScreenLayout
      title={getMessage('label.configuration.help.and.instructions.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <HelpInstructionsConfigForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              formDirty={formDirty}
              disableKeyField={initialValues.key.length !== 0}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
